import { ContextKindKey } from '@gonfalon/contexts/types';
import type { GuardedReleasePhaseStatus } from '@gonfalon/flags/assertGuardedReleasePhaseStatus';
import { getAllTargetsForVariation } from '@gonfalon/flags/getAllTargetsForVariation';
import { getCountOfTargetsForVariation } from '@gonfalon/flags/getCountOfTargetsForVariation';
import { fromJS, List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';

import type { AccessChecks } from '#utils/access/types';
import { idOrKey } from '#utils/flags/idOrKey';
import { createFallthrough, Fallthrough } from '#utils/flags/records/Fallthrough';
import type { Prerequisite } from '#utils/flags/records/Prerequisite';
import { createPrerequisite } from '#utils/flags/records/Prerequisite';
import type { Rule } from '#utils/flags/records/Rule';
import { createRule } from '#utils/flags/records/Rule';
import type { Target } from '#utils/flags/records/Target';
import { createTarget } from '#utils/flags/records/Target';
import type { TargetingSummary } from '#utils/flags/records/TargetingSummary';
import { createTargetingSummary } from '#utils/flags/records/TargetingSummary';
import type { Link } from '#utils/linkUtils';

export type FlagConfigMigrationSettings = {
  checkRatio: number;
};

type FlagContextsEvaluationProps = {
  contextKinds: List<string>;
};
export class FlagContextsEvaluation extends Record<FlagContextsEvaluationProps>({
  contextKinds: List(),
}) {}

export function createContextsEvaluation(props?: CreateFunctionInput<FlagContextsEvaluation>) {
  return props instanceof FlagContextsEvaluation ? props : new FlagContextsEvaluation(fromJS(props));
}

type FlagConfigurationProps = {
  on: boolean;
  archived: boolean;
  salt: string;
  sel: string;
  lastModified: number;
  version: number;
  contextTargets: List<Target>;
  targets: List<Target>;
  rules: List<Rule>;
  fallthrough: Fallthrough;
  offVariation?: number;
  prerequisites: List<Prerequisite>;
  _site: Link;
  _environmentName: string;
  trackEvents: boolean;
  trackEventsFallthrough: boolean;
  _debugEventsUntilDate?: number;
  _summary?: TargetingSummary;
  _access?: AccessChecks;
  evaluation?: FlagContextsEvaluation;
  migrationSettings?: Map<string, FlagConfigMigrationSettings>;
  measuredRolloutStatus?: GuardedReleasePhaseStatus;
};

const flagConfigSettings = {
  on: false,
  contextTargets: List(),
  targets: List(),
  rules: List(),
  fallthrough: new Fallthrough(),
  prerequisites: List(),
  offVariation: undefined,
};

export type FlagConfigSetting = keyof typeof flagConfigSettings;

export class FlagConfiguration extends Record<FlagConfigurationProps>({
  ...flagConfigSettings,
  _site: Map(),
  archived: false,
  salt: '',
  sel: '',
  version: 0,
  trackEventsFallthrough: false,
  trackEvents: false,
  _environmentName: '',
  lastModified: 0,
  _debugEventsUntilDate: undefined,
  _summary: undefined,
  _access: undefined,
  evaluation: undefined,
  migrationSettings: undefined,
  measuredRolloutStatus: undefined,
}) {
  getTarget(variationIndex: number) {
    const index = this.targets.findIndex((t) => t.variation === variationIndex);
    return index === -1 ? undefined : this.targets.get(index);
  }

  getContextTarget(variationIndex: number, contextKind: string) {
    const index = this.contextTargets.findIndex((t) => t.variation === variationIndex && t.contextKind === contextKind);
    return index === -1 ? undefined : this.contextTargets.get(index);
  }
  // Combines user and context targets
  getAllTargets() {
    let allTargets = this.contextTargets;
    const variationToUserTargetValues: { [key: number]: List<string> } = {};
    this.targets.forEach((t) => {
      // Build map of variation to user target values
      if (variationToUserTargetValues.hasOwnProperty(t.variation)) {
        variationToUserTargetValues[t.variation] = variationToUserTargetValues[t.variation].merge(t.values);
      } else {
        variationToUserTargetValues[t.variation] = t.values;
      }
      // Account for user targets not represented in contextTargets
      const isFoundInContextTargets = allTargets.find(
        (c) => c.contextKind === ContextKindKey.USER && c.variation === t.variation,
      );
      if (!isFoundInContextTargets) {
        allTargets = allTargets.push(t);
      }
    });

    return allTargets.map((t) => {
      let target = t;
      if ((t.contextKind === ContextKindKey.USER || '') && variationToUserTargetValues.hasOwnProperty(t.variation)) {
        target = target.set('values', variationToUserTargetValues[t.variation].sort());
      }
      return target;
    });
  }

  getAllTargetsForVariation(variationIdx: number) {
    const allTargets = getAllTargetsForVariation(this.toJS(), variationIdx);
    const targetRecords = allTargets.map((t) => createTarget(t));
    return List(targetRecords);
  }

  // Returns all values regardless of context kind
  getTargetValuesForVariation(variationIdx: number) {
    const targets = this.getAllTargetsForVariation(variationIdx);
    let targetValues: List<string> = List();
    targets.forEach((target) => {
      targetValues = targetValues.concat(target.values);
    });
    return targetValues;
  }

  getCountOfTargetsForVariation(variationIdx: number) {
    return getCountOfTargetsForVariation(this.toJS(), variationIdx);
  }

  getRuleById(ruleId: string) {
    return this.rules.filter((r) => idOrKey(r) === ruleId).first<undefined>();
  }

  getRuleIndex(ruleKey: string | undefined) {
    return this.rules.findIndex((r) => r._key === ruleKey);
  }

  hasTargets(variationIndex: number) {
    const targets = this.getTarget(variationIndex);
    const contextTargetsIndex = this.contextTargets.findIndex((t) => t.variation === variationIndex);
    const contextTargets = contextTargetsIndex === -1 ? undefined : this.contextTargets.get(contextTargetsIndex);

    return (targets && !targets.isEmpty()) || (contextTargets && !contextTargets.isEmpty());
  }

  countTargetingRules() {
    const numberOfTargets = this.targets.isEmpty() ? 0 : 1;
    const numberOfCustomRules = this.rules.size;
    const fallthrough = 1; //fallthrough always exists and there is only one
    return numberOfTargets + numberOfCustomRules + fallthrough;
  }

  countTargetingRulesTrackingEvents() {
    if (this.trackEvents) {
      return this.countTargetingRules();
    }

    if (!this.trackEvents) {
      let count = 0;
      if (this.trackEventsFallthrough) {
        count++;
      }
      if (this.rules.size) {
        this.rules.forEach((rule) => {
          if (rule.trackEvents) {
            count++;
          }
        });
      }
      return count;
    }

    return 0;
  }

  toRep() {
    return fromJS(this.toJSON()).withMutations((map: FlagConfiguration) => {
      map.get('offVariation') === undefined && map.remove('offVariation');
      map.update('fallthrough', (f) => f.toRep());
      map.update('rules', (rs) => rs.map((r) => r.toRep()));
      map.update('prerequisites', (pr) => pr.map((p) => p.toRep()));
    });
  }
}

export function createFlagConfiguration(props: CreateFunctionInput<FlagConfiguration> = {}) {
  const cfg = props instanceof FlagConfiguration ? props : new FlagConfiguration(fromJS(props));
  return cfg.withMutations((c) => {
    c.update('targets', (ts) => ts?.map(createTarget));
    c.update('contextTargets', (ct) => ct?.map(createTarget));
    c.update('fallthrough', createFallthrough);
    c.update('rules', (rs) => rs && rs.map(createRule));
    c.update('prerequisites', (ps) => (ps ? ps.map(createPrerequisite) : List()));
    c.update('_summary', createTargetingSummary);
    c.update('evaluation', createContextsEvaluation);
  });
}
